import { DBSchema, IDBPDatabase, openDB } from 'idb'

export type AnalyticsUsageDay = {
  measurementId: string
  usageDay: string
  elapsedTimeMs: number
  foregroundTimeMs: number
}

export type AnalyticsMeasurementCheckpoint = {
  // The object store is unchanged. Readers reject legacy unsegmented records
  // rather than guessing which day (or suspended period) their totals cover.
  version: 2
  measurementId: string
  appId: string
  appName: string
  appPath: string
  path: string
  userId?: number
  organizationId?: number
  originalOrganizationId?: number | null
  days: AnalyticsUsageDay[]
  createdAt: number
  updatedAt: number
}

interface AnalyticsTrackingDBSchema extends DBSchema {
  measurements: {
    key: string
    value: AnalyticsMeasurementCheckpoint
    indexes: { updatedAt: number }
  }
}

type DB = IDBPDatabase<AnalyticsTrackingDBSchema>

const DB_NAME = 'analytics-tracking'
const STORE = 'measurements' as const
const DB_VERSION = 1

function openAnalyticsTrackingDB(): Promise<DB> {
  return openDB<AnalyticsTrackingDBSchema>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, {
          keyPath: 'measurementId'
        })
        store.createIndex('updatedAt', 'updatedAt')
      }
    },
    // A newer version opening in another tab must not wait on this one. The
    // next call reopens (and fails cleanly if the schema has moved on).
    blocking(_currentVersion, _blockedVersion, event) {
      ;(event.target as IDBDatabase).close()
    }
  })
}

export class AnalyticsTrackingDB {
  private db: Promise<DB>

  constructor() {
    this.db = openAnalyticsTrackingDB()
  }

  private async withDB<T>(fn: (db: DB) => Promise<T>): Promise<T> {
    try {
      return await fn(await this.db)
    } catch (error) {
      if (error instanceof DOMException && error.name === 'InvalidStateError') {
        this.db = openAnalyticsTrackingDB()
        return fn(await this.db)
      }
      throw error
    }
  }

  async write(checkpoint: AnalyticsMeasurementCheckpoint): Promise<void> {
    await this.withDB(db => db.put(STORE, checkpoint))
  }

  async read(
    measurementId: string
  ): Promise<AnalyticsMeasurementCheckpoint | undefined> {
    return this.withDB(db => db.get(STORE, measurementId))
  }

  async remove(measurementId: string): Promise<void> {
    await this.withDB(db => db.delete(STORE, measurementId))
  }

  async prune(maxAgeMs: number): Promise<void> {
    await this.withDB(async db => {
      const tx = db.transaction(STORE, 'readwrite')
      let cursor = await tx.store
        .index('updatedAt')
        .openCursor(IDBKeyRange.upperBound(Date.now() - maxAgeMs))

      while (cursor) {
        // IndexedDB cursors must be advanced sequentially.
        // eslint-disable-next-line no-await-in-loop
        await cursor.delete()
        // eslint-disable-next-line no-await-in-loop
        cursor = await cursor.continue()
      }

      await tx.done
    })
  }

  static devWarn(context: string, error: unknown): void {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[AnalyticsTrackingDB] ${context}:`, error)
    }
  }
}
